import React, { RefObject, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

// Components
import EditPageSortableList from 'components/manage/website/entities/EditPageCommon/EditPageSortableList';
import Modal from '@zola/zola-ui/src/components/Modal/Modal';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';

// Types
import type { ZolaFileResponseType } from 'actions/types/UploadcareActions';
import type { GenericEntityType } from 'actions/types/website/EntityActionTypes';
import type { WCmsPhotoView } from '@zola/svc-web-api-ts-client';

// Reducers
import { useAppDispatch } from 'reducers/index';

// Actions
import { moveEntity } from 'actions/website/EntityActions';
import { toastsActions } from '@zola-helpers/client';
import { updateMultiplePhotos } from 'actions/website/entities/PhotosActions.js';

// Utils
import { getImageUrl } from 'cards/util/imageHelper';
import { entityNames } from 'actions/PublicWebsiteActions';
import { buildUpdatePhotosPayload } from 'components/common/MultiPhotoUpload/utils';

// Styles
import { PhotoEntityPreview } from 'components/manage/website/entities/EditPageCommon/EditPageSortableList/EditPageSortableList.styles';
import {
  ActionsContainer,
  ContentContainer,
  Header,
  ListContainer,
} from './AddPhotoDetailsModal.styles';

const PhotoDetails = ({ image_id }: GenericEntityType = {}): JSX.Element => (
  <>{image_id && <PhotoEntityPreview image={getImageUrl(image_id)} />}</>
);

type AddPhotoDetailsModalProps = {
  handleModalClose: () => void;
  modalTriggerRef: RefObject<HTMLButtonElement> | null;
  photos: Record<number, WCmsPhotoView>;
  removePhotoFromMultiQueue: (id: number) => void;
  updatePhotoCaptionInMultiQueue: (id: number, newCaption: string) => void;
  updatePhotoInMultiQueue: (id: number, newPhoto: ZolaFileResponseType) => void;
  updateMultiPhotosQueueKey: (key: string) => void;
  photoOrderedIdsFullList: number[];
};

const AddPhotoDetailsModal: React.FC<AddPhotoDetailsModalProps> = ({
  handleModalClose,
  modalTriggerRef,
  photos,
  removePhotoFromMultiQueue,
  updatePhotoCaptionInMultiQueue,
  updatePhotoInMultiQueue,
  updateMultiPhotosQueueKey,
  photoOrderedIdsFullList,
}) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const numOfPhotos = Object.keys(photos).length;
    if (numOfPhotos === 0) {
      handleModalClose();
    }
  }, [photos]);

  const updateMultiPhotosQueueIndexChange = (
    oldId: number,
    newId: number,
    entityApiName?: string
  ) => {
    const oldIndexInFullList = photoOrderedIdsFullList.indexOf(oldId);
    const newIndexInFullList = photoOrderedIdsFullList.indexOf(newId);
    dispatch(moveEntity(oldIndexInFullList, newIndexInFullList, entityApiName));
    updateMultiPhotosQueueKey(`multiPhotosQueueKey-${uuidv4()}`); // ensure rerender of parent DnD component, won't update otherwise due to the list relying on local state
  };

  const handleSavePhotos = () => {
    try {
      const payload = buildUpdatePhotosPayload({
        photosQueue: photos,
      });
      dispatch(updateMultiplePhotos(payload));
      handleModalClose();
    } catch (error) {
      dispatch(toastsActions.negative({ headline: 'Caption too long' }));
    }
  };

  const renderDnDPhotosList = (): React.ReactNode | undefined => {
    if (!photos || Object.keys(photos).length === 0) return undefined;
    const orderedIds = Object.keys(photos).map(id => Number(id));
    return (
      <EditPageSortableList
        entityDetailProps={{
          entityDetail: PhotoDetails,
          entityEditRemoveName: 'photo',
        }}
        entityApiName={entityNames.photos}
        orderedIds={orderedIds}
        entities={photos}
        isMultiPhoto
        removePhotoFromMultiQueue={removePhotoFromMultiQueue}
        updatePhotoInMultiQueue={updatePhotoInMultiQueue}
        updatePhotoCaptionInMultiQueue={updatePhotoCaptionInMultiQueue}
        updateMultiPhotosQueueIndexChange={updateMultiPhotosQueueIndexChange}
      />
    );
  };

  return (
    <Modal
      dialogHeadingId="AddPhotoDetailsHeader"
      onClose={handleModalClose}
      rootId="zola-wedding-container"
      triggerRef={modalTriggerRef}
    >
      <ContentContainer>
        <Header id="AddPhotoDetailsHeader">Add photo details</Header>
        <ListContainer>{renderDnDPhotosList()}</ListContainer>
        <ActionsContainer>
          <ButtonV3 type="button" variant="primary" onClick={handleSavePhotos}>
            Save
          </ButtonV3>
        </ActionsContainer>
      </ContentContainer>
    </Modal>
  );
};

export default AddPhotoDetailsModal;
